/**
 * @file API utility wrappers.
 * @description Shared fetch helpers for auth headers, response parsing, token-expiry handling, and URL helpers.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { API_BASE_URL, HTTP_METHODS, STORAGE_KEYS } from '../constants/api';
import { HttpMethod, ApiError, ApiResponse } from '../types';
import { isTokenExpired, createTokenExpiredError, isTokenError } from './tokenValidation';
import { showTokenExpiredNotification } from './notificationService';

let tokenRedirectTimeout: number | null = null;

/**
 * Builds a complete API URL from an endpoint
 */
export const buildApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  return `${API_BASE_URL}/${cleanEndpoint}`;
};

/**
 * Gets the authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

/**
 * Sets the authentication token in localStorage
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
};

/**
 * Removes the authentication token from localStorage
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};

/**
 * Handles token expiration by clearing auth data and redirecting to login
 */
export const handleTokenExpiration = (message?: string): void => {
  // Clear all authentication data
  removeAuthToken();
  localStorage.removeItem(STORAGE_KEYS.USER);
  
  // Show user-friendly notification
  showTokenExpiredNotification(message);
  
  // Redirect to login after a short delay
  if (tokenRedirectTimeout) {
    window.clearTimeout(tokenRedirectTimeout);
  }

  tokenRedirectTimeout = window.setTimeout(() => {
    window.location.href = '/login';
  }, 2000);
};

/**
 * Creates headers for API requests
 */
export const createHeaders = (): Headers => {
  const headers = new Headers();
  const token = getAuthToken();
  
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }
  
  return headers;
};

/**
 * Handles API response and error parsing
 */
export const handleApiResponse = async <T>(response: Response): Promise<T> => {
  let responseData: unknown;

  try {
    responseData = await response.json();
  } catch {
    responseData = { message: 'Invalid JSON response from server' };
  }

  if (!response.ok) {
    const errorMessage = 
      (typeof responseData === 'object' &&
        responseData !== null &&
        'message' in responseData &&
        typeof (responseData as { message: unknown }).message === 'string')
        ? (responseData as { message: string }).message
        : `API error: ${response.status} - ${response.statusText}`;

    // Check if this is a token expiration error
    if (responseData && typeof responseData === 'object' && 'status' in responseData) {
      const apiResponse = responseData as ApiResponse;
      if (isTokenExpired(apiResponse)) {
        const tokenError = createTokenExpiredError(errorMessage);
        throw tokenError;
      }
    }

    const apiError: ApiError = {
      message: errorMessage,
      status: response.status,
    };

    throw apiError;
  }

  return responseData as T;
};

/**
 * Main API fetch function with proper error handling
 */
export async function apiFetch<T>(
  endpoint: string,
  method: HttpMethod = HTTP_METHODS.GET,
  body?: FormData | URLSearchParams | Record<string, unknown>,
  responseType: 'json' | 'blob' = 'json',
): Promise<T> {
  const headers = createHeaders();
  const options: RequestInit = { method, headers };

  // Handle request body
  if (body instanceof FormData || body instanceof URLSearchParams) {
    options.body = body;
  } else if (body) {
    headers.append('Content-Type', 'application/json');
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(buildApiUrl(endpoint), options);
    if (responseType === 'blob') {
      if (!response.ok) {
        let errorData: unknown;
        try {
          errorData = await response.json();
        } catch {
          errorData = await response.text();
        }
        const errorMessage =
          typeof errorData === 'object' &&
          errorData !== null &&
          'message' in errorData &&
          typeof (errorData as { message: unknown }).message === 'string'
            ? (errorData as { message: string }).message
            : 'Failed to download file';
        throw new Error(errorMessage);
      }
      return (await response.blob()) as unknown as T;
    }
    return await handleApiResponse<T>(response);
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Network error or server is unreachable.');
    }
    // Check if this is a token error that requires logout
    if (isTokenError(error)) {
      // Trigger auto-logout
      handleTokenExpiration();
      throw error;
    }
    throw error;
  }
}

/**
 * Utility function to create FormData from an object
 */
export const createFormData = (data: Record<string, unknown>): FormData => {
  const formData = new FormData();
  
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  
  return formData;
};

/**
 * Utility function to build query string from filters
 */
export const buildQueryString = (params: Record<string, string | number>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
};
